import { type MotionValue, useMotionValue, useMotionValueEvent, useReducedMotion, useScroll } from "framer-motion";
import { useLayoutEffect, useRef } from "react";
import { SECTION_THREE_ENTRY_LEAD_DESKTOP_UNITS } from "../three/use-section-three-scroll";

/** S3 rides document scroll (1.0). S2 recedes slower during the cover so the handoff reads with depth. */
export const SECTION_TWO_HANDOFF_FACTOR = 0.5;

export function mapSectionTwoHandoffY({
	scrollY,
	s3RunwayTop,
	viewportHeight,
	entryLeadUnits = SECTION_THREE_ENTRY_LEAD_DESKTOP_UNITS,
	handoffFactor = SECTION_TWO_HANDOFF_FACTOR,
	reducedMotion = false,
}: {
	scrollY: number;
	s3RunwayTop: number;
	viewportHeight: number;
	entryLeadUnits?: number;
	handoffFactor?: number;
	reducedMotion?: boolean;
}): number {
	if (reducedMotion) return 0;
	const viewport = Math.max(viewportHeight, 1);
	const unitsFromPin = (scrollY - s3RunwayTop) / viewport;
	const unitsFromEntry = unitsFromPin + entryLeadUnits;
	if (unitsFromEntry <= 0) return 0;
	return -unitsFromEntry * viewport * handoffFactor;
}

/**
 * Drives the desktop S2 accordion recede while S3 slides over the settled pin-hold.
 * Timing matches S3's entry lead so both handoffs start at first intersection.
 */
export function useSectionTwoHandoffParallax(): MotionValue<number> {
	const reducedMotion = useReducedMotion();
	const reducedMotionRef = useRef(reducedMotion);
	reducedMotionRef.current = reducedMotion;
	const handoffY = useMotionValue(0);
	const { scrollY } = useScroll();
	const layoutCacheRef = useRef<{ s3Runway: HTMLElement | null; viewportHeight: number }>({
		s3Runway: null,
		viewportHeight: 1,
	});

	const refreshLayoutCache = () => {
		if (typeof window === "undefined") return;
		const s3Runway = document.querySelector<HTMLElement>(".landing-section-three-runway");
		layoutCacheRef.current = {
			s3Runway,
			viewportHeight: Math.max(window.innerHeight, 1),
		};
	};

	const refreshLayoutCacheRef = useRef(refreshLayoutCache);
	refreshLayoutCacheRef.current = refreshLayoutCache;

	const applyHandoff = () => {
		if (typeof window === "undefined") return;
		let { s3Runway, viewportHeight } = layoutCacheRef.current;
		if (!s3Runway) {
			refreshLayoutCacheRef.current();
			s3Runway = layoutCacheRef.current.s3Runway;
			viewportHeight = layoutCacheRef.current.viewportHeight;
		}
		if (!s3Runway) {
			handoffY.set(0);
			return;
		}
		handoffY.set(
			mapSectionTwoHandoffY({
				scrollY: scrollY.get(),
				s3RunwayTop: s3Runway.offsetTop,
				viewportHeight,
				reducedMotion: Boolean(reducedMotionRef.current),
			}),
		);
	};

	const applyHandoffRef = useRef(applyHandoff);
	applyHandoffRef.current = applyHandoff;
	const applyHandoffRafRef = useRef(0);

	useMotionValueEvent(scrollY, "change", () => {
		if (applyHandoffRafRef.current !== 0) return;
		applyHandoffRafRef.current = window.requestAnimationFrame(() => {
			applyHandoffRafRef.current = 0;
			applyHandoffRef.current();
		});
	});

	useLayoutEffect(() => {
		const s3Runway = document.querySelector<HTMLElement>(".landing-section-three-runway");
		const onLayoutChange = () => {
			refreshLayoutCacheRef.current();
			applyHandoffRef.current();
		};
		onLayoutChange();
		window.addEventListener("resize", onLayoutChange);
		const observer = typeof ResizeObserver !== "undefined" && s3Runway ? new ResizeObserver(onLayoutChange) : null;
		observer?.observe(s3Runway as HTMLElement);
		return () => {
			window.removeEventListener("resize", onLayoutChange);
			observer?.disconnect();
			if (applyHandoffRafRef.current !== 0) {
				window.cancelAnimationFrame(applyHandoffRafRef.current);
				applyHandoffRafRef.current = 0;
			}
		};
	}, []);

	return handoffY;
}
